import React, { use, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, FormControl, FormLabel, FormControlLabel, RadioGroup, Radio, TextField } from '@mui/material';
import { Controller, set, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormGroup, Switch } from '@mui/material';
import { AprovarOrcamentoFormData, aprovarOrcamentoSchema } from '../schema/aprovar-orcamento.schema';
import InputData from '../../../shared/components/formulario/input-data/inputData';
import InputMascarado from '../../../shared/components/formulario/input-mascarado/InputMascarado';
import { mascaraMoedaReal } from '../../../shared/utils/mascaras/mascaraMoedaReal';
import InputPlanoDeContas from '../../../shared/components/formulario/inputPlanoDeContas/inputPlanoDeContas';
import { MovementTypeEnum } from '../../financeiro/schema/gerar-movimentacao-obra.schema';
import InputSetores from '../../../shared/components/formulario/inputSetores/inputSetor';
import { useMutation } from '@tanstack/react-query';
import { AprovarOrcamentoContract } from '../contracts/aprovar-orcamento.contracts';
import { useLoader } from '../../../shared/contexts/LoaderContext';
import { orcamentoService } from '../service/orcamento.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { InputSwitch } from '../../../shared/components/formulario/input-switch/switch';


interface AprovarOrcamentoModalProps {
    open: boolean;
    idOrcamento: string;
    onClose: () => void;
    onSubmit: (data: AprovarOrcamentoFormData) => void;
    defaultValues?: Partial<AprovarOrcamentoFormData>;
    loading?: boolean;
}

export const AprovarOrcamentoModal: React.FC<AprovarOrcamentoModalProps> = ({
    open,
    onClose,
    onSubmit,
    defaultValues = {},
    loading = false,
    idOrcamento
}) => {
    const methods = useForm<AprovarOrcamentoFormData>({
        resolver: yupResolver(aprovarOrcamentoSchema),
        defaultValues: {
            isToWorkflow: false,
            createAccountsReceivable: false,
            isDownPaymentValue: false,
            ...defaultValues,
        },
    });

    const { handleSubmit, reset, formState: { errors }, register, control, watch } = methods;
    const { showLoader, hideLoader } = useLoader();
    const isToWorkflow = watch("isToWorkflow") === true;
    const createAccountsReceivable = watch("createAccountsReceivable") === true;
    const isDownPaymentValue = watch("isDownPaymentValue") === true;
    const navigate = useNavigate();

    let mensagem = "Orçamento aprovado com sucesso.";
    if (isToWorkflow) {
        mensagem = "Orçamento aprovado e obra gerada com sucesso.";
        if (createAccountsReceivable) {
            mensagem = "Orçamento aprovado, obra gerada e contas a receber criadas com sucesso. ";
        }
        if (createAccountsReceivable && isDownPaymentValue) {
            mensagem = "Orçamento aprovado, obra gerada e contas a receber criadas com sucesso. Para registrar o valor de entrada, efetue manualmente a movimentação de entrada no caixa.";
        }
    }

    React.useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
    }, [open]);

    const { mutate } = useMutation<unknown, Error, AprovarOrcamentoContract>({
        mutationFn: async (data) => {
            showLoader();
            return await orcamentoService.aprovarOrcamento(idOrcamento, data);
        },
        onSuccess: () => {
            hideLoader();
            onClose();
            toast.success(mensagem);
            navigate("/orcamentos", { state: { reload: true } });
        },
        onError: (err: any) => {
            hideLoader();
            toast.error(err?.mensagem || "Erro ao aprovar orçamento.");
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Aprovar Orçamento</DialogTitle>
            <Box component="form" onSubmit={handleSubmit((data: AprovarOrcamentoContract) => mutate(data))}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <InputSwitch
                                name="isToWorkflow"
                                label="Deseja gerar uma obra a partir dessa aprovação?"
                                methods={methods}
                            />

                        </Grid>
                        {isToWorkflow && (
                            <Grid item xs={12} md={6}>
                                <InputData
                                    name="startDate"
                                    label="Data de início da obra"
                                    methods={methods}
                                    helperText={errors.startDate?.message}
                                />
                            </Grid>
                        )}
                        {isToWorkflow && (
                            <Grid item xs={12} md={6}>
                                <InputData
                                    name="endDate"
                                    label="Data de término da obra"
                                    methods={methods}
                                    helperText={errors.endDate?.message}
                                />
                            </Grid>
                        )}

                        {isToWorkflow && (
                            <Grid item xs={12} md={6}>
                                <InputSwitch
                                    name="createAccountsReceivable"
                                    label="Deseja gerar contas a receber a partir deste orçamento?"
                                    methods={methods}
                                />
                            </Grid>
                        )}

                        {isToWorkflow && createAccountsReceivable && (
                            <Grid item xs={12} md={6}>
                                <InputSwitch
                                    name="isDownPaymentValue"
                                    label="Deseja informar um valor de entrada?"
                                    methods={methods}
                                />
                            </Grid>
                        )}
                        {isToWorkflow && createAccountsReceivable && isDownPaymentValue && (
                            <Grid item xs={12} md={6}>
                                <InputMascarado
                                    name="downPaymentValue"
                                    methods={methods}
                                    label="Valor de entrada"
                                    mask={mascaraMoedaReal}
                                    helperText={errors.downPaymentValue?.message}
                                />
                            </Grid>
                        )}

                        {isToWorkflow && createAccountsReceivable && (
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name={`installmentCount`}
                                    control={control}
                                    defaultValue={0}
                                    render={({ field }) => <TextField {...field} label="Número de parcelas" type="number" size="small" fullWidth />}
                                />
                            </Grid>
                        )}

                        {isToWorkflow && createAccountsReceivable && (
                            <Grid item xs={12} md={6}>
                                <InputData
                                    name="dueDate"
                                    label="Data de vencimento da primeira parcela"
                                    methods={methods}
                                    helperText={errors.dueDate?.message}
                                />
                            </Grid>
                        )}
                        {isToWorkflow && createAccountsReceivable && (
                            <Grid item xs={12} md={6}>
                                <InputPlanoDeContas
                                    name="chartOfAccountsId"
                                    label="Plano de Contas"
                                    tiPosMovimentacao={MovementTypeEnum.INCOME || ""}
                                    methods={methods}
                                />
                            </Grid>
                        )}
                        {isToWorkflow && createAccountsReceivable && (
                            <Grid item xs={12} md={6}>
                                <InputSetores
                                    name="idSector"
                                    label="Setor de Origem"
                                    methods={methods}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                        {loading ? 'Aprovando...' : 'Aprovar'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
